import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Affiliates = () => {
  // Navigation tabs
  const [activeTab, setActiveTab] = useState('affiliates'); // 'affiliates' | 'payouts' | 'referrals'

  // Data states
  const [affiliates, setAffiliates] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({
    totalAffiliates: 0,
    totalSales: 0,
    totalCommissions: 0,
    totalPendingPayouts: 0,
    totalClicks: 0,
    totalConversions: 0
  });

  // Loading & error states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Payout Filters
  const [payoutStatusFilter, setPayoutStatusFilter] = useState('all');

  // Referral Filters
  const [referralSearch, setReferralSearch] = useState('');

  // Modals state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [affiliateToApprove, setAffiliateToApprove] = useState(null);
  const [approving, setApproving] = useState(false);
  const [approveForm, setApproveForm] = useState({
    commissionRate: 10,
    discountPercentage: 10,
    couponCode: ''
  });

  // Selected item states
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [affiliateToDelete, setAffiliateToDelete] = useState(null);

  // Form states - Add Affiliate
  const [addForm, setAddForm] = useState({
    name: '',
    email: '',
    phone: '',
    customCode: '',
    commissionRate: 10,
    discountPercentage: 10,
    audienceCategory: 'Content creator',
    organizationOrClub: '',
    city: '',
    state: '',
    audienceLocation: 'Mostly India',
    expectedReferrals: '5-20 per month',
    whyJoin: '',
    howPromote: '',
    pastExperience: '',
    upiId: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    youtube: '',
    instagram: '',
    linkedin: ''
  });

  // Form states - Edit Affiliate
  const [editForm, setEditForm] = useState({
    status: 'active',
    commissionRate: 10,
    discountPercentage: 10,
    audienceCategory: 'Student',
    notes: ''
  });

  // Form states - Process Payout
  const [payoutForm, setPayoutForm] = useState({
    status: 'completed',
    transactionReference: '',
    notes: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  // Helper for auth header
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  // Fetch all affiliates & stats
  const fetchAffiliates = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/affiliates/admin/all`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) {
        setAffiliates(data.affiliates || []);
        if (data.stats) {
          setStats((prev) => ({
            ...prev,
            ...data.stats
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching affiliates:', err);
      // Fallback: try singular endpoint
      try {
        const fallbackRes = await fetch(`${API_BASE_URL}/api/affiliate/admin/all`, {
          headers: getAuthHeaders()
        });
        if (fallbackRes.ok) {
          const fallbackData = await fallbackRes.json();
          if (fallbackData.success) {
            setAffiliates(fallbackData.affiliates || []);
            if (fallbackData.stats) setStats(fallbackData.stats);
          }
        }
      } catch (fallbackErr) {
        console.error('Fallback fetch also failed:', fallbackErr);
      }
    }
  }, []);

  // Fetch all payouts
  const fetchPayouts = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/affiliates/admin/payouts`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setPayouts(data.payouts || []);
        }
      }
    } catch (err) {
      console.error('Error fetching payouts:', err);
    }
  }, []);

  // Fetch all referrals
  const fetchReferrals = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/affiliates/admin/referrals`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setReferrals(data.referrals || []);
        }
      }
    } catch (err) {
      console.error('Error fetching referrals:', err);
    }
  }, []);

  // Load all initial data
  const loadAllData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      await Promise.all([fetchAffiliates(), fetchPayouts(), fetchReferrals()]);
      if (isRefresh) {
        toast.success('Affiliate data refreshed successfully');
      }
    } catch (err) {
      console.error('Failed to load affiliate data:', err);
      setError('Failed to connect to affiliate backend services.');
      toast.error('Failed to load data from server.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchAffiliates, fetchPayouts, fetchReferrals]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Copy referral code / link to clipboard
  const handleCopy = (text, identifier) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(identifier);
    toast.info(`Copied to clipboard: ${text}`);
    setTimeout(() => setCopiedCode(null), 2500);
  };

  // Open Edit Modal
  const handleOpenEdit = (affiliate) => {
    setSelectedAffiliate(affiliate);
    setEditForm({
      status: affiliate.status || 'active',
      referralCode: affiliate.referralCode || '',
      commissionRate: affiliate.commissionRate ?? 10,
      discountPercentage: affiliate.discountPercentage ?? 10,
      audienceCategory: affiliate.audienceCategory || 'Content creator',
      notes: affiliate.notes || ''
    });
    setShowEditModal(true);
  };

  // Submit Edit Affiliate
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedAffiliate) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/affiliates/admin/${selectedAffiliate._id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(editForm)
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Updated affiliate settings for ${selectedAffiliate.name}`);
        setShowEditModal(false);
        fetchAffiliates();
      } else {
        toast.error(data.message || 'Failed to update affiliate');
      }
    } catch (err) {
      toast.error('Network error updating affiliate');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Add Affiliate
  const handleAddAffiliate = async (e) => {
    e.preventDefault();
    if (!addForm.name || !addForm.email) {
      toast.warn('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: addForm.name,
        email: addForm.email,
        phone: addForm.phone,
        customCode: addForm.customCode,
        commissionRate: Number(addForm.commissionRate) || 10,
        discountPercentage: Number(addForm.discountPercentage) || 10,
        audienceCategory: addForm.audienceCategory,
        organizationOrClub: addForm.organizationOrClub,
        city: addForm.city,
        state: addForm.state,
        audienceLocation: addForm.audienceLocation,
        expectedReferrals: addForm.expectedReferrals,
        whyJoin: addForm.whyJoin,
        howPromote: addForm.howPromote,
        pastExperience: addForm.pastExperience,
        payoutInfo: {
          upiId: addForm.upiId,
          accountHolderName: addForm.accountHolderName,
          accountNumber: addForm.accountNumber,
          ifscCode: addForm.ifscCode,
          bankName: addForm.bankName
        },
        socialLinks: {
          youtube: addForm.youtube,
          instagram: addForm.instagram,
          linkedin: addForm.linkedin
        }
      };

      const res = await fetch(`${API_BASE_URL}/api/affiliates/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();

      if (data.success) {
        toast.success(data.message || 'Affiliate registered successfully!');
        setShowAddModal(false);
        setAddForm({
          name: '',
          email: '',
          phone: '',
          customCode: '',
          commissionRate: 10,
          discountPercentage: 10,
          audienceCategory: 'Content creator',
          organizationOrClub: '',
          city: '',
          state: '',
          audienceLocation: 'Mostly India',
          expectedReferrals: '5-20 per month',
          whyJoin: '',
          howPromote: '',
          pastExperience: '',
          upiId: '',
          accountHolderName: '',
          accountNumber: '',
          ifscCode: '',
          bankName: '',
          youtube: '',
          instagram: '',
          linkedin: ''
        });
        fetchAffiliates();
      } else {
        toast.error(data.message || 'Error registering affiliate');
      }
    } catch (err) {
      toast.error('Network error creating affiliate');
    } finally {
      setSubmitting(false);
    }
  };


  // Open modal to configure custom commission, discount & coupon code before approving
  const handleOpenApproveModal = (affiliate) => {
    setAffiliateToApprove(affiliate);
    setApproveForm({
      commissionRate: affiliate.commissionRate !== undefined && affiliate.commissionRate !== null ? affiliate.commissionRate : 10,
      discountPercentage: affiliate.discountPercentage !== undefined && affiliate.discountPercentage !== null ? affiliate.discountPercentage : 10,
      couponCode: '' // Starts blank so admin writes their own referral / coupon code!
    });
    setShowApproveModal(true);
  };

  // Submit Approval with manual commission, discount & coupon code
  const handleConfirmApprove = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!affiliateToApprove) return;

    const commRate = Number(approveForm.commissionRate);
    const discRate = Number(approveForm.discountPercentage);
    const code = (approveForm.couponCode || '').trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '');

    if (!code || code.length < 3) {
      toast.warn('Please enter a referral / coupon code for this affiliate (at least 3 characters)');
      return;
    }

    if (isNaN(commRate) || commRate < 0 || commRate > 100) {
      toast.warn('Please enter a valid commission rate between 0% and 100%');
      return;
    }

    if (isNaN(discRate) || discRate < 0 || discRate > 100) {
      toast.warn('Please enter a valid discount percentage between 0% and 100%');
      return;
    }

    setApproving(true);
    try {
      let res = await fetch(`${API_BASE_URL}/api/affiliates/admin/${affiliateToApprove._id}/approve`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          commissionRate: commRate,
          discountPercentage: discRate,
          couponCode: code
        })
      });
      if (!res.ok) {
        // Fallback to singular
        res = await fetch(`${API_BASE_URL}/api/affiliate/admin/${affiliateToApprove._id}/approve`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            commissionRate: commRate,
            discountPercentage: discRate,
            couponCode: code
          })
        });
      }
      const data = await res.json();
      if (data.success) {
        toast.success(`🎉 Affiliate "${affiliateToApprove.name}" APPROVED with code "${code}", ${commRate}% commission & ${discRate}% discount! Coupon is now LIVE.`);
        fetchAffiliates();
        setShowApproveModal(false);
        setAffiliateToApprove(null);
        if (showDetailModal) setShowDetailModal(false);
      } else {
        toast.error(data.message || 'Failed to approve affiliate');
      }
    } catch (err) {
      toast.error('Network error approving affiliate');
    } finally {
      setApproving(false);
    }
  };

  // Approve affiliate partner callback (triggers modal)
  const handleApproveAffiliate = (affiliate) => {
    handleOpenApproveModal(affiliate);
  };

  // Reject affiliate partner & deactivate coupon
  const handleRejectAffiliate = async (affiliate) => {
    const reason = window.prompt(`Enter rejection reason for "${affiliate.name}" (optional):`, 'Application did not meet current criteria');
    if (reason === null) return; // cancelled

    try {
      let res = await fetch(`${API_BASE_URL}/api/affiliates/admin/${affiliate._id}/reject`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason })
      });
      if (!res.ok) {
        res = await fetch(`${API_BASE_URL}/api/affiliate/admin/${affiliate._id}/reject`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ reason })
        });
      }
      const data = await res.json();
      if (data.success) {
        toast.info(`❌ Affiliate "${affiliate.name}" rejected. Coupon disabled.`);
        fetchAffiliates();
        if (showDetailModal) setShowDetailModal(false);
      } else {
        toast.error(data.message || 'Failed to reject affiliate');
      }
    } catch (err) {
      toast.error('Network error rejecting affiliate');
    }
  };

  // Quick toggle affiliate status
  const handleToggleStatus = async (affiliate) => {
    const newStatus = affiliate.status === 'active' ? 'suspended' : 'active';
    try {
      const res = await fetch(`${API_BASE_URL}/api/affiliates/admin/${affiliate._id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Affiliate marked as ${newStatus}`);
        fetchAffiliates();
      } else {
        toast.error(data.message || 'Failed to update status');
      }
    } catch (err) {
      toast.error('Error changing status');
    }
  };

  // Delete affiliate
  const handleDeleteAffiliate = async () => {
    if (!affiliateToDelete) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/affiliates/admin/${affiliateToDelete._id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Affiliate ${affiliateToDelete.name} deleted`);
        setShowDeleteModal(false);
        setAffiliateToDelete(null);
        fetchAffiliates();
      } else {
        toast.error(data.message || 'Failed to delete affiliate');
      }
    } catch (err) {
      toast.error('Network error during deletion');
    } finally {
      setSubmitting(false);
    }
  };

  // Open Process Payout Modal
  const handleOpenProcessPayout = (payout, defaultStatus = 'completed') => {
    setSelectedPayout(payout);
    setPayoutForm({
      status: defaultStatus,
      transactionReference: payout.transactionReference || '',
      notes: payout.notes || ''
    });
    setShowPayoutModal(true);
  };

  // Submit Payout Status Change
  const handleSubmitPayout = async (e) => {
    e.preventDefault();
    if (!selectedPayout) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/affiliates/admin/payouts/${selectedPayout._id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(payoutForm)
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Payout #${selectedPayout._id.slice(-6)} marked as ${payoutForm.status}`);
        setShowPayoutModal(false);
        fetchPayouts();
        fetchAffiliates();
      } else {
        toast.error(data.message || 'Failed to update payout');
      }
    } catch (err) {
      toast.error('Network error updating payout');
    } finally {
      setSubmitting(false);
    }
  };

  // Filtered & Sorted Affiliates
  const filteredAffiliates = useMemo(() => {
    return affiliates
      .filter((aff) => {
        // Search filter
        const query = searchTerm.toLowerCase();
        const matchesSearch =
          !query ||
          aff.name?.toLowerCase().includes(query) ||
          aff.email?.toLowerCase().includes(query) ||
          aff.phone?.toLowerCase().includes(query) ||
          aff.referralCode?.toLowerCase().includes(query) ||
          aff.city?.toLowerCase().includes(query) ||
          aff.state?.toLowerCase().includes(query) ||
          aff.audienceCategory?.toLowerCase().includes(query) ||
          aff.organizationOrClub?.toLowerCase().includes(query);

        // Status filter
        const matchesStatus = statusFilter === 'all' || aff.status === statusFilter;

        // Category filter
        const matchesCategory = categoryFilter === 'all' || aff.audienceCategory === categoryFilter;

        return matchesSearch && matchesStatus && matchesCategory;
      })
      .sort((a, b) => {
        if (sortBy === 'newest') {
          return new Date(b.createdAt) - new Date(a.createdAt);
        }
        if (sortBy === 'sales') {
          return (b.metrics?.totalSalesVolume || 0) - (a.metrics?.totalSalesVolume || 0);
        }
        if (sortBy === 'conversions') {
          return (b.metrics?.totalConversions || 0) - (a.metrics?.totalConversions || 0);
        }
        if (sortBy === 'commission') {
          return (b.metrics?.totalCommissionEarned || 0) - (a.metrics?.totalCommissionEarned || 0);
        }
        if (sortBy === 'pendingPayout') {
          return (b.metrics?.pendingCommission || 0) - (a.metrics?.pendingCommission || 0);
        }
        return 0;
      });
  }, [affiliates, searchTerm, statusFilter, categoryFilter, sortBy]);

  // Filtered Payouts
  const filteredPayouts = useMemo(() => {
    return payouts.filter((p) => {
      if (payoutStatusFilter === 'all') return true;
      return p.status === payoutStatusFilter;
    });
  }, [payouts, payoutStatusFilter]);

  // Filtered Referrals
  const filteredReferrals = useMemo(() => {
    return referrals.filter((r) => {
      if (!referralSearch) return true;
      const q = referralSearch.toLowerCase();
      return (
        r.referralCode?.toLowerCase().includes(q) ||
        r.buyerName?.toLowerCase().includes(q) ||
        r.buyerEmailMasked?.toLowerCase().includes(q) ||
        r.productName?.toLowerCase().includes(q) ||
        r.razorpayPaymentId?.toLowerCase().includes(q) ||
        (r.affiliateId && r.affiliateId.name?.toLowerCase().includes(q))
      );
    });
  }, [referrals, referralSearch]);

  // Derived counts
  const pendingAffiliatesCount = affiliates.filter((a) => a.status === 'pending').length;
  const pendingPayoutCount = payouts.filter((p) => p.status === 'requested' || p.status === 'processing').length;
  const pendingPayoutTotal = payouts
    .filter((p) => p.status === 'requested' || p.status === 'processing')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  const conversionRate = stats.totalClicks > 0
    ? ((stats.totalConversions / stats.totalClicks) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span>⚠️</span>
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 font-bold text-sm">
            ✕
          </button>
        </div>
      )}

      {/* ── Top Header ── */}

      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center space-x-3">
            <span className="text-3xl">🤝</span>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
              Affiliate Marketing & Partners
            </h1>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Monitor partner referrals, analyze sales conversion performance, and handle affiliate payouts.
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => loadAllData(true)}
            disabled={refreshing}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors focus:outline-none"
            title="Refresh all affiliate data"
          >
            <svg
              className={`h-4 w-4 mr-2 text-gray-500 ${refreshing ? 'animate-spin text-blue-600' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>

          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6-6H6" />
            </svg>
            Add Affiliate Partner
          </button>
        </div>
      </div>

      {/* ── Metric Cards ── */}
            {/* Pending Affiliate Approvals Attention Banner */}
      {pendingAffiliatesCount > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-sm">
          <div className="flex items-center space-x-3">
            <span className="p-2 bg-amber-100 text-amber-700 rounded-lg text-xl">⏳</span>
            <div>
              <h4 className="text-sm font-bold text-amber-900 flex items-center gap-2">
                <span>{pendingAffiliatesCount} Affiliate Application{pendingAffiliatesCount > 1 ? 's' : ''} Awaiting Approval</span>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-extrabold bg-amber-200 text-amber-900">Action Required</span>
              </h4>
              <p className="text-xs text-amber-700 mt-0.5">
                New applicants cannot receive referral codes or earn commissions until you approve them and assign their custom rates below.
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setActiveTab('affiliates');
              setStatusFilter('pending');
            }}
            className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition-colors shadow-sm shrink-0"
          >
            Review Pending ({pendingAffiliatesCount})
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {/* Total Affiliates */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Total Affiliates</span>
            <span className="p-2 bg-blue-50 text-blue-600 rounded-lg text-lg">👥</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-900">{stats.totalAffiliates}</p>
          <div className="mt-2 flex items-center text-xs text-gray-500">
            <span className="text-emerald-600 font-semibold mr-1">
              {affiliates.filter((a) => a.status === 'active').length} Active
            </span>
            {pendingAffiliatesCount > 0 && (
              <span className="text-amber-600 font-bold bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 mr-1">
                ⏳ {pendingAffiliatesCount} Pending
              </span>
            )}
            <span>• {affiliates.filter((a) => a.status === 'suspended').length} Suspended</span>
          </div>
        </div>

        {/* Total Sales Volume */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Referral Revenue</span>
            <span className="p-2 bg-emerald-50 text-emerald-600 rounded-lg text-lg">💰</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-900">₹{(stats.totalSales || 0).toLocaleString('en-IN')}</p>
          <p className="mt-2 text-xs text-gray-500">Gross revenue driven</p>
        </div>

        {/* Total Commission Earned */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Total Commission</span>
            <span className="p-2 bg-purple-50 text-purple-600 rounded-lg text-lg">💳</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-purple-700">₹{(stats.totalCommissions || 0).toLocaleString('en-IN')}</p>
          <p className="mt-2 text-xs text-gray-500">All-time commissions earned</p>
        </div>

        {/* Pending Payouts */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-amber-200 bg-gradient-to-br from-amber-50/50 to-white">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Pending Payouts</span>
            <span className="p-2 bg-amber-100 text-amber-600 rounded-lg text-lg">⏳</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-amber-600">
            ₹{(stats.totalPendingPayouts || pendingPayoutTotal || 0).toLocaleString('en-IN')}
          </p>
          <div className="mt-2 text-xs text-amber-700 font-medium">
            {pendingPayoutCount > 0 ? `${pendingPayoutCount} requests awaiting review` : 'All payouts cleared'}
          </div>
        </div>

        {/* Clicks & Conversions */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Conversions</span>
            <span className="p-2 bg-indigo-50 text-indigo-600 rounded-lg text-lg">🎯</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-900">{stats.totalConversions || 0}</p>
          <p className="mt-2 text-xs text-gray-500">
            from {stats.totalClicks || 0} clicks ({conversionRate}% rate)
          </p>
        </div>
      </div>

      {/* ── Navigation Tabs ── */}
      <div className="mb-6 border-b border-gray-200 bg-white rounded-xl shadow-sm px-4">
        <nav className="flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('affiliates')}
            className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'affiliates'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="mr-2">👥</span>
            All Affiliates
            <span className={`ml-2 py-0.5 px-2 rounded-full text-xs font-medium ${
              activeTab === 'affiliates' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {affiliates.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('payouts')}
            className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'payouts'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="mr-2">💸</span>
            Payout Requests
            {pendingPayoutCount > 0 ? (
              <span className="ml-2 py-0.5 px-2 rounded-full text-xs font-bold bg-amber-100 text-amber-800 animate-pulse">
                {pendingPayoutCount} Pending
              </span>
            ) : (
              <span className="ml-2 py-0.5 px-2 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                {payouts.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('referrals')}
            className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'referrals'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="mr-2">🧾</span>
            Referral Conversions
            <span className={`ml-2 py-0.5 px-2 rounded-full text-xs font-medium ${
              activeTab === 'referrals' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {referrals.length}
            </span>
          </button>
        </nav>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          TAB 1: ALL AFFILIATES DIRECTORY
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'affiliates' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Filters & Search Toolbar */}
          <div className="p-4 sm:p-5 border-b border-gray-100 bg-gray-50/50 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1 max-w-md relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, referral code..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Status filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-medium"
              >
                <option value="all">Status: All</option>
                <option value="pending">⏳ Pending Approval ({pendingAffiliatesCount})</option>
                <option value="active">✅ Active (Live Coupons)</option>
                <option value="suspended">⚠️ Suspended</option>
                <option value="rejected">❌ Rejected</option>
              </select>

              {/* Category filter */}
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Category: All</option>
                <option value="Content creator">Content creator</option>
                <option value="Student">Student</option>
                <option value="Robotics Creator">Robotics Creator</option>
                <option value="Tech Influencer">Tech Influencer</option>
                <option value="YouTuber">YouTuber</option>
                <option value="College Club">College Club</option>
                <option value="Educator">Educator</option>
                <option value="Mentor">Mentor</option>
                <option value="Robotics Community">Robotics Community</option>
                <option value="Other">Other</option>
              </select>

              {/* Sort by */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="newest">Sort: Newest</option>
                <option value="sales">Sort: Highest Sales</option>
                <option value="conversions">Sort: Most Conversions</option>
                <option value="commission">Sort: Highest Commission</option>
                <option value="pendingPayout">Sort: Pending Balance</option>
              </select>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="p-12 text-center text-gray-500">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent mb-3" />
              <p>Loading affiliate partners...</p>
            </div>
          ) : filteredAffiliates.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <span className="text-4xl">🔍</span>
              <p className="mt-2 font-medium text-gray-700">No affiliates found</p>
              <p className="text-sm text-gray-400 mt-1">
                {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
                  ? 'Try clearing your search query or changing filters.'
                  : 'Get started by clicking "Add Affiliate Partner" above.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Partner / Affiliate
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Referral Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Rates
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Performance
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Commissions (₹)
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAffiliates.map((aff) => {
                    const initials = aff.name
                      ? aff.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .slice(0, 2)
                          .toUpperCase()
                      : 'AF';

                    return (
                      <tr key={aff._id} className="hover:bg-gray-50/80 transition-colors">
                        {/* Name & Contact */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
                              {initials}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-semibold text-gray-900">{aff.name}</div>
                              <div className="text-xs text-gray-500">{aff.email}</div>
                              {(aff.city || aff.state) && (
                                <div className="text-[11px] text-gray-400 flex items-center space-x-1">
                                  <span>📍</span>
                                  <span>{[aff.city, aff.state].filter(Boolean).join(', ')}</span>
                                </div>
                              )}
                              {aff.organizationOrClub && (
                                <div className="text-xs text-indigo-600 font-medium">{aff.organizationOrClub}</div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Referral Code */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {aff.status === 'pending' ? (
                            <div className="flex items-center space-x-1.5">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                                ⏳ Not assigned yet
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold font-mono bg-blue-50 text-blue-700 border border-blue-200">
                                {aff.referralCode}
                              </span>
                              <button
                                onClick={() => handleCopy(aff.referralCode, aff._id)}
                                className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                                title="Copy code"
                              >
                                {copiedCode === aff._id ? (
                                  <span className="text-xs text-emerald-600 font-bold">✓ Copied</span>
                                ) : (
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          )}
                          <div className="text-xs text-gray-500 mt-1 flex items-center space-x-1.5 flex-wrap">
                            <span className="inline-block px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-[10px] font-medium">
                              {aff.audienceCategory || 'Content creator'}
                            </span>
                            {aff.channels && aff.channels.length > 0 && (
                              <span className="text-[10px] text-blue-600 font-semibold" title={`${aff.channels.length} promotion channels`}>
                                • {aff.channels.length} {aff.channels.length === 1 ? 'channel' : 'channels'}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Rates */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {aff.status === 'pending' ? (
                            <div>
                              <div className="text-xs text-amber-700 font-semibold flex items-center space-x-1">
                                <span>⏳</span>
                                <span>Set on approval</span>
                              </div>
                              <div className="text-[11px] text-gray-400">
                                Awaiting admin review
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="text-xs font-medium text-gray-900">
                                Commission: <span className="text-emerald-600 font-bold">{aff.commissionRate ?? 0}%</span>
                              </div>
                              <div className="text-xs text-gray-500">
                                Discount: <span className="text-blue-600 font-semibold">{aff.discountPercentage ?? 0}% OFF</span>
                              </div>
                            </>
                          )}
                        </td>

                        {/* Performance Stats */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            ₹{(aff.metrics?.totalSalesVolume || 0).toLocaleString('en-IN')}
                          </div>
                          <div className="text-xs text-gray-500">
                            {aff.metrics?.totalConversions || 0} orders • {aff.metrics?.totalClicks || 0} clicks
                          </div>
                        </td>

                        {/* Commission Balances */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-gray-900">
                            ₹{(aff.metrics?.totalCommissionEarned || 0).toLocaleString('en-IN')}
                          </div>
                          <div className="text-xs flex items-center space-x-2 mt-0.5">
                            <span className="text-amber-600 font-medium" title="Pending withdrawal balance">
                              Pending: ₹{(aff.metrics?.pendingCommission || 0).toLocaleString('en-IN')}
                            </span>
                            <span>•</span>
                            <span className="text-gray-400" title="Paid out so far">
                              Paid: ₹{(aff.metrics?.paidCommission || 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {aff.status === 'active' && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
                              <span className="h-1.5 w-1.5 mr-1.5 rounded-full bg-emerald-600 animate-pulse" />
                              Active (Coupon Live)
                            </span>
                          )}
                          {aff.status === 'pending' && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300 animate-pulse">
                              <span className="h-2 w-2 mr-1.5 rounded-full bg-amber-500" />
                              Pending Approval
                            </span>
                          )}
                          {aff.status === 'suspended' && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-300">
                              Suspended
                            </span>
                          )}
                          {aff.status === 'rejected' && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
                              Rejected
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-1.5">
                          {aff.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleOpenApproveModal(aff)}
                                className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold shadow-sm transition-colors inline-flex items-center space-x-1"
                                title="Approve affiliate application and set manual commission & discount"
                              >
                                <span>✓</span>
                                <span>Approve</span>
                              </button>
                              <button
                                onClick={() => handleRejectAffiliate(aff)}
                                className="px-2.5 py-1 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg text-xs font-semibold transition-colors inline-flex items-center space-x-1"
                                title="Reject affiliate application"
                              >
                                <span>✕</span>
                                <span>Reject</span>
                              </button>
                            </>
                          )}

                          <button
                            onClick={() => {
                              setSelectedAffiliate(aff);
                              setShowDetailModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 px-2 py-1 bg-blue-50 rounded hover:bg-blue-100 transition-colors text-xs"
                            title="View details & banking info"
                          >
                            Details
                          </button>

                          <button
                            onClick={() => handleOpenEdit(aff)}
                            className="text-gray-700 hover:text-gray-900 px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 transition-colors text-xs"
                            title="Edit rates & status"
                          >
                            Edit
                          </button>

                          {aff.status === 'active' && (
                            <button
                              onClick={() => handleToggleStatus(aff)}
                              className="px-2 py-1 rounded transition-colors text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100"
                              title="Suspend affiliate coupon"
                            >
                              Suspend
                            </button>
                          )}

                          {(aff.status === 'suspended' || aff.status === 'rejected') && (
                            <button
                              onClick={() => handleOpenApproveModal(aff)}
                              className="px-2 py-1 rounded transition-colors text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                              title="Re-activate affiliate and customize commission & discount"
                            >
                              Re-Approve
                            </button>
                          )}

                          <button
                            onClick={() => {
                              setAffiliateToDelete(aff);
                              setShowDeleteModal(true);
                            }}
                            className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-colors"
                            title="Delete affiliate"
                          >
                            <svg className="h-4 w-4 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 2: PAYOUT REQUESTS MANAGEMENT
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'payouts' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Header toolbar */}
          <div className="p-4 sm:p-5 border-b border-gray-100 bg-gray-50/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-base font-bold text-gray-900">Affiliate Withdrawal Requests</h2>
              <p className="text-xs text-gray-500">
                Review and approve payout disbursements to affiliates via UPI or Direct Bank Transfer
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <span className="text-xs text-gray-500 font-medium">Filter by Status:</span>
              <select
                value={payoutStatusFilter}
                onChange={(e) => setPayoutStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="requested">Requested (Pending)</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed (Paid)</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          {/* Table */}
          {filteredPayouts.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <span className="text-4xl">💸</span>
              <p className="mt-2 font-medium text-gray-700">No payout requests found</p>
              <p className="text-xs text-gray-400 mt-1">
                When affiliates request withdrawals from their dashboard (minimum ₹500), they will appear here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Request Date & ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Affiliate Partner
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Amount Requested
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Method & Payment Details
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Process Payout
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPayouts.map((p) => {
                    const aff = p.affiliateId || {};
                    return (
                      <tr key={p._id} className="hover:bg-gray-50/80 transition-colors">
                        {/* Date & ID */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {new Date(p.createdAt).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </div>
                          <div className="text-xs text-gray-400 font-mono">#{p._id.slice(-8)}</div>
                        </td>

                        {/* Affiliate */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">{aff.name || 'Affiliate'}</div>
                          <div className="text-xs text-gray-500">{aff.email || '—'}</div>
                          {aff.referralCode && (
                            <span className="inline-block mt-0.5 text-xs font-mono font-bold text-blue-600 bg-blue-50 px-1.5 py-0.2 rounded">
                              {aff.referralCode}
                            </span>
                          )}
                        </td>

                        {/* Amount */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-lg font-bold text-gray-900">₹{p.amount?.toLocaleString('en-IN')}</div>
                        </td>

                        {/* Method & Details */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                            {p.payoutMethod === 'upi' ? '⚡ UPI Transfer' : '🏦 Bank Transfer'}
                          </div>
                          {p.payoutDetails?.upiId ? (
                            <div className="flex items-center space-x-1.5">
                              <span className="text-xs font-mono font-medium text-gray-800 bg-gray-100 px-2 py-0.5 rounded">
                                {p.payoutDetails.upiId}
                              </span>
                              <button
                                onClick={() => handleCopy(p.payoutDetails.upiId, p._id)}
                                className="text-gray-400 hover:text-blue-600 text-xs p-1"
                                title="Copy UPI ID"
                              >
                                {copiedCode === p._id ? '✓' : '📋'}
                              </button>
                            </div>
                          ) : p.payoutDetails?.accountNumber ? (
                            <div className="text-xs text-gray-600">
                              <div>{p.payoutDetails.bankName || 'Bank'} • A/C: {p.payoutDetails.accountNumber}</div>
                              <div className="text-gray-400">IFSC: {p.payoutDetails.ifscCode} | {p.payoutDetails.accountHolderName}</div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 italic">No payout details provided</span>
                          )}

                          {p.transactionReference && (
                            <div className="mt-1 text-xs text-emerald-700 font-mono">
                              Ref: {p.transactionReference}
                            </div>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {p.status === 'requested' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                              Requested
                            </span>
                          )}
                          {p.status === 'processing' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                              Processing
                            </span>
                          )}
                          {p.status === 'completed' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                              Completed
                            </span>
                          )}
                          {p.status === 'rejected' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                              Rejected
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          {p.status === 'requested' && (
                            <>
                              <button
                                onClick={() => handleOpenProcessPayout(p, 'completed')}
                                className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold shadow-sm transition-colors"
                              >
                                Approve & Pay
                              </button>
                              <button
                                onClick={() => handleOpenProcessPayout(p, 'rejected')}
                                className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded text-xs font-semibold transition-colors"
                              >
                                Reject
                              </button>
                            </>
                          )}

                          {p.status === 'processing' && (
                            <button
                              onClick={() => handleOpenProcessPayout(p, 'completed')}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold shadow-sm transition-colors"
                            >
                              Mark Completed
                            </button>
                          )}

                          {p.status === 'completed' && (
                            <button
                              onClick={() => handleOpenProcessPayout(p, 'completed')}
                              className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs font-medium transition-colors"
                            >
                              View Ref
                            </button>
                          )}

                          {p.status === 'rejected' && (
                            <span className="text-xs text-gray-400 italic">No action needed</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 3: REFERRAL CONVERSIONS LOG
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'referrals' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Header toolbar */}
          <div className="p-4 sm:p-5 border-b border-gray-100 bg-gray-50/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-base font-bold text-gray-900">Converted Orders & Referral Attribution</h2>
              <p className="text-xs text-gray-500">
                Audit trail of successful checkout orders credited to affiliate partner codes
              </p>
            </div>

            <div className="max-w-xs w-full">
              <input
                type="text"
                value={referralSearch}
                onChange={(e) => setReferralSearch(e.target.value)}
                placeholder="Search by code, buyer, product..."
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Table */}
          {filteredReferrals.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <span className="text-4xl">🧾</span>
              <p className="mt-2 font-medium text-gray-700">No referral conversion orders yet</p>
              <p className="text-xs text-gray-400 mt-1">
                When students purchase Takumi plans using an affiliate referral code or link, orders will appear here automatically.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Affiliate Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Buyer Info
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Order Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Commission Credited
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredReferrals.map((ref) => (
                    <tr key={ref._id} className="hover:bg-gray-50/80 transition-colors">
                      {/* Date */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {new Date(ref.createdAt).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                        <div className="text-xs text-gray-400">
                          {new Date(ref.createdAt).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </td>

                      {/* Affiliate Code */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-bold bg-blue-50 text-blue-700 border border-blue-200">
                          {ref.referralCode}
                        </span>
                        {ref.affiliateId && (
                          <div className="text-xs text-gray-500 mt-0.5">{ref.affiliateId.name}</div>
                        )}
                      </td>

                      {/* Buyer */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{ref.buyerName || 'Student'}</div>
                        <div className="text-xs text-gray-500">{ref.buyerEmailMasked || '—'}</div>
                      </td>

                      {/* Product */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-800">{ref.productName || 'Takumi Plan'}</div>
                        {ref.razorpayPaymentId && (
                          <div className="text-xs text-gray-400 font-mono">PID: {ref.razorpayPaymentId.slice(-8)}</div>
                        )}
                      </td>

                      {/* Order Value */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900">₹{ref.orderAmount?.toLocaleString('en-IN')}</div>
                        {ref.discountGiven > 0 && (
                          <div className="text-xs text-emerald-600">Saved: ₹{ref.discountGiven}</div>
                        )}
                      </td>

                      {/* Commission */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-purple-700">
                          ₹{ref.commissionAmount?.toLocaleString('en-IN')}
                        </div>
                        <div className="text-xs text-gray-400">{ref.commissionRate}% cut</div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          ref.status === 'approved' || ref.status === 'paid'
                            ? 'bg-emerald-100 text-emerald-800'
                            : ref.status === 'pending'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {ref.status || 'approved'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL: ADD NEW AFFILIATE PARTNER
      ───────────────────────────────────────────────────────────── */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in duration-200">
            <div className="p-6 bg-gradient-to-r from-blue-600 to-indigo-700 text-white flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold">Register New Affiliate Partner</h3>
                <p className="text-xs text-blue-100 mt-0.5">
                  Generate referral codes, assign custom commission rates, and activate coupon discounts.
                </p>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-white hover:text-gray-200 p-1 text-2xl font-light focus:outline-none"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddAffiliate} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Partner Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={addForm.name}
                    onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                    placeholder="e.g. Rahul Sharma"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={addForm.email}
                    onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                    placeholder="rahul@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="text"
                    value={addForm.phone}
                    onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
                    placeholder="+91 9876543210"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Custom Referral Code (Optional)
                  </label>
                  <input
                    type="text"
                    value={addForm.customCode}
                    onChange={(e) => setAddForm({ ...addForm, customCode: e.target.value.toUpperCase() })}
                    placeholder="e.g. TAKUMI-RAHUL (auto if empty)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono uppercase focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Affiliate Commission Rate (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={addForm.commissionRate}
                    onChange={(e) => setAddForm({ ...addForm, commissionRate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="text-xs text-gray-400">Default: 10% earned by partner</span>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Buyer Discount Percentage (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={addForm.discountPercentage}
                    onChange={(e) => setAddForm({ ...addForm, discountPercentage: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="text-xs text-gray-400">Default: 10% discount for buyers</span>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Audience Category</label>
                  <select
                    value={addForm.audienceCategory}
                    onChange={(e) => setAddForm({ ...addForm, audienceCategory: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="Content creator">Content creator</option>
                    <option value="Student">Student</option>
                    <option value="Robotics Creator">Robotics Creator</option>
                    <option value="Tech Influencer">Tech Influencer</option>
                    <option value="YouTuber">YouTuber</option>
                    <option value="College Club">College Club</option>
                    <option value="Educator">Educator</option>
                    <option value="Mentor">Mentor</option>
                    <option value="Robotics Community">Robotics Community</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Club / Organization Name</label>
                  <input
                    type="text"
                    value={addForm.organizationOrClub}
                    onChange={(e) => setAddForm({ ...addForm, organizationOrClub: e.target.value })}
                    placeholder="e.g. IIT Delhi Robotics Club"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={addForm.city}
                    onChange={(e) => setAddForm({ ...addForm, city: e.target.value })}
                    placeholder="e.g. Mumbai"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={addForm.state}
                    onChange={(e) => setAddForm({ ...addForm, state: e.target.value })}
                    placeholder="e.g. Maharashtra"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Payout Details Section */}
              <div className="pt-3 border-t border-gray-100">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                  Payout Information (Optional)
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">UPI ID (e.g. name@okhdfcbank)</label>
                    <input
                      type="text"
                      value={addForm.upiId}
                      onChange={(e) => setAddForm({ ...addForm, upiId: e.target.value })}
                      placeholder="rahul@upi"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Bank Name</label>
                    <input
                      type="text"
                      value={addForm.bankName}
                      onChange={(e) => setAddForm({ ...addForm, bankName: e.target.value })}
                      placeholder="HDFC Bank"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Account Number</label>
                    <input
                      type="text"
                      value={addForm.accountNumber}
                      onChange={(e) => setAddForm({ ...addForm, accountNumber: e.target.value })}
                      placeholder="501002345678"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">IFSC Code</label>
                    <input
                      type="text"
                      value={addForm.ifscCode}
                      onChange={(e) => setAddForm({ ...addForm, ifscCode: e.target.value.toUpperCase() })}
                      placeholder="HDFC0001234"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase"
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="pt-4 border-t border-gray-100 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Registering...' : 'Create Affiliate Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL: EDIT AFFILIATE SETTINGS
      ───────────────────────────────────────────────────────────── */}
      {showEditModal && selectedAffiliate && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden border border-gray-100">
            <div className="p-5 bg-gray-900 text-white flex justify-between items-center">
              <div>
                <h3 className="text-base font-bold">Edit Affiliate: {selectedAffiliate.name}</h3>
                <p className="text-xs text-gray-400 font-mono mt-0.5">
                  {selectedAffiliate.status === 'pending' ? 'Pending Approval (No active code)' : selectedAffiliate.referralCode}
                </p>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-white p-1 text-xl focus:outline-none"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Affiliate Account Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white font-medium"
                >
                  <option value="active">Active (Coupon live & usable at checkout)</option>
                  <option value="pending">Pending (Awaiting admin approval, coupon blocked)</option>
                  <option value="suspended">Suspended (Coupon temporarily disabled)</option>
                  <option value="rejected">Rejected (Application rejected)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Coupon / Referral Code</label>
                <input
                  type="text"
                  value={editForm.referralCode || ''}
                  onChange={(e) => setEditForm({ ...editForm, referralCode: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono font-bold uppercase bg-gray-50 text-gray-900"
                />
                <span className="text-[11px] text-gray-400">Coupon code is synced automatically with active store discounts</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Commission Rate (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={editForm.commissionRate}
                    onChange={(e) => setEditForm({ ...editForm, commissionRate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Buyer Discount (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={editForm.discountPercentage}
                    onChange={(e) => setEditForm({ ...editForm, discountPercentage: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Audience Category</label>
                <select
                  value={editForm.audienceCategory}
                  onChange={(e) => setEditForm({ ...editForm, audienceCategory: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  <option value="Content creator">Content creator</option>
                  <option value="Student">Student</option>
                  <option value="Robotics Creator">Robotics Creator</option>
                  <option value="Tech Influencer">Tech Influencer</option>
                  <option value="YouTuber">YouTuber</option>
                  <option value="College Club">College Club</option>
                  <option value="Educator">Educator</option>
                  <option value="Mentor">Mentor</option>
                  <option value="Robotics Community">Robotics Community</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Admin Notes / Remarks</label>
                <textarea
                  rows="3"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Internal notes about this partner..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              <div className="pt-4 border-t border-gray-100 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL: VIEW AFFILIATE DETAILS DEEP DIVE (WITH ALL APPLICANT FIELDS)
      ───────────────────────────────────────────────────────────── */}
      {showDetailModal && selectedAffiliate && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl overflow-hidden border border-gray-100 animate-fade-in">
            {/* Header */}
            <div className="p-6 bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800 text-white flex justify-between items-start">
              <div>
                <div className="flex items-center space-x-2.5 flex-wrap">
                  <h3 className="text-xl font-bold tracking-tight">{selectedAffiliate.name}</h3>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                    selectedAffiliate.status === 'active'
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : selectedAffiliate.status === 'pending'
                      ? 'bg-amber-400 text-gray-900 shadow-sm animate-pulse'
                      : selectedAffiliate.status === 'suspended'
                      ? 'bg-orange-500 text-white'
                      : 'bg-red-500 text-white'
                  }`}>
                    {selectedAffiliate.status}
                  </span>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white backdrop-blur-sm">
                    {selectedAffiliate.audienceCategory || 'Content creator'}
                  </span>
                </div>
                <div className="flex items-center space-x-3 text-xs text-blue-100 mt-1.5 flex-wrap">
                  <span>✉️ {selectedAffiliate.email}</span>
                  {selectedAffiliate.phone && <span>📞 {selectedAffiliate.phone}</span>}
                  {(selectedAffiliate.city || selectedAffiliate.state) && (
                    <span>📍 {[selectedAffiliate.city, selectedAffiliate.state].filter(Boolean).join(', ')}</span>
                  )}
                </div>
                {selectedAffiliate.organizationOrClub && (
                  <div className="text-xs text-blue-200 mt-1 font-medium">
                    🏫 Organization / Club: <strong className="text-white">{selectedAffiliate.organizationOrClub}</strong>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 text-xl font-bold focus:outline-none transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
              {/* Partner Referral Code & Rates Overview */}
              {selectedAffiliate.status === 'pending' ? (
                <div className="p-4 bg-amber-50/90 border border-amber-200 rounded-xl flex items-start justify-between gap-4 shadow-sm">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl mt-0.5">⏳</span>
                    <div>
                      <h4 className="text-xs font-bold text-amber-900 uppercase tracking-wider">
                        Awaiting Admin Approval & Coupon Assignment
                      </h4>
                      <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                        No coupon code, commission rate, or buyer discount has been assigned to <strong>{selectedAffiliate.name}</strong> yet. You can review their application details below, then click &quot;Approve &amp; Configure Rates&quot; to assign custom rates and write a coupon code.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDetailModal(false);
                      handleOpenApproveModal(selectedAffiliate);
                    }}
                    className="shrink-0 px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg shadow-sm text-xs transition-colors flex items-center space-x-1"
                  >
                    <span>✓</span>
                    <span>Approve Now</span>
                  </button>
                </div>
              ) : (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between shadow-sm">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-emerald-800 tracking-wider">Active Partner Coupon &amp; Rates</span>
                    <div className="flex items-center space-x-2.5 mt-1">
                      <span className="font-mono text-sm font-extrabold text-emerald-950 bg-white px-2.5 py-0.5 rounded border border-emerald-300">
                        {selectedAffiliate.referralCode}
                      </span>
                      <span className="text-xs text-emerald-800 font-semibold">
                        • Commission: <strong>{selectedAffiliate.commissionRate ?? 0}%</strong> • Discount: <strong>{selectedAffiliate.discountPercentage ?? 0}% OFF</strong>
                      </span>
                    </div>
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-0.5 bg-emerald-200 text-emerald-900 rounded-full">
                    Active &amp; Live
                  </span>
                </div>
              )}

              {/* ── NEW SECTION: Applicant Profile & Strategy Answers ── */}
              <div className="bg-slate-50/70 p-4 rounded-xl border border-gray-200/80 space-y-3.5">
                <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center space-x-1.5">
                  <span>📝</span>
                  <span>Application Details & Marketing Strategy</span>
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  {/* Location Info */}
                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                    <span className="text-gray-400 font-semibold block text-[10px] uppercase">Applicant Location</span>
                    <div className="font-semibold text-gray-800 mt-0.5">
                      {[selectedAffiliate.city, selectedAffiliate.state].filter(Boolean).join(', ') || 'Not specified'}
                    </div>
                    <span className="text-[11px] text-gray-500 mt-0.5 block">
                      Audience Base: {selectedAffiliate.audienceLocation || 'Mostly India'}
                    </span>
                  </div>

                  {/* Reach & Expected Referrals */}
                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                    <span className="text-gray-400 font-semibold block text-[10px] uppercase">Estimated Reach & Referrals</span>
                    <div className="font-semibold text-emerald-700 mt-0.5">
                      {selectedAffiliate.expectedReferrals || '5-20 per month'}
                    </div>
                    {selectedAffiliate.averageEngagement && (
                      <span className="text-[11px] text-gray-500 mt-0.5 block">
                        Avg Engagement: <strong>{selectedAffiliate.averageEngagement}</strong>
                      </span>
                    )}
                  </div>
                </div>

                {/* Target Audience Tags */}
                {selectedAffiliate.targetAudience && selectedAffiliate.targetAudience.length > 0 && (
                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                    <span className="text-gray-400 font-semibold block text-[10px] uppercase mb-1.5">Target Audience Segments</span>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedAffiliate.targetAudience.map((audience, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-md text-xs font-medium border border-indigo-100">
                          🎯 {audience}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Why Join */}
                {selectedAffiliate.whyJoin && (
                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                    <span className="text-gray-400 font-semibold block text-[10px] uppercase mb-1">Why do they want to join?</span>
                    <p className="text-xs text-gray-700 italic leading-relaxed">
                      "{selectedAffiliate.whyJoin}"
                    </p>
                  </div>
                )}

                {/* How to Promote */}
                {selectedAffiliate.howPromote && (
                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                    <span className="text-gray-400 font-semibold block text-[10px] uppercase mb-1">Promotion Plan & Channels</span>
                    <p className="text-xs text-gray-700 leading-relaxed">
                      {selectedAffiliate.howPromote}
                    </p>
                  </div>
                )}

                {/* Past Experience */}
                {selectedAffiliate.pastExperience && (
                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                    <span className="text-gray-400 font-semibold block text-[10px] uppercase mb-1">Past Affiliate / Influencer Experience</span>
                    <p className="text-xs text-gray-700 leading-relaxed">
                      {selectedAffiliate.pastExperience}
                    </p>
                  </div>
                )}
              </div>

              {/* ── NEW SECTION: Channels & Social Media Handles ── */}
              <div>
                <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-2.5 flex items-center space-x-1.5">
                  <span>📱</span>
                  <span>Promotion Channels & Social Handles</span>
                </h4>

                {/* Channels Array */}
                {selectedAffiliate.channels && selectedAffiliate.channels.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {selectedAffiliate.channels.map((ch, idx) => (
                      <div key={idx} className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
                        <div className="flex-1 min-w-0 pr-2">
                          <div className="flex items-center space-x-1.5">
                            <span className="text-xs font-bold text-gray-900 capitalize">{ch.platform || 'Platform'}</span>
                            {ch.followers && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 font-semibold">
                                {ch.followers}
                              </span>
                            )}
                          </div>
                          {ch.link && (
                            <a
                              href={ch.link.startsWith('http') ? ch.link : `https://${ch.link}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline truncate block mt-0.5"
                            >
                              🔗 {ch.link}
                            </a>
                          )}
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                          ch.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {ch.enabled ? 'Active' : 'Listed'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                    No custom platform channels specified in the application.
                  </p>
                )}

                {/* Direct Social Links (if any) */}
                {selectedAffiliate.socialLinks && (
                  <div className="flex flex-wrap gap-2 mt-2.5">
                    {selectedAffiliate.socialLinks.youtube && (
                      <a
                        href={selectedAffiliate.socialLinks.youtube.startsWith('http') ? selectedAffiliate.socialLinks.youtube : `https://${selectedAffiliate.socialLinks.youtube}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1 bg-red-50 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-100 transition-colors inline-flex items-center space-x-1"
                      >
                        <span>▶️ YouTube</span>
                      </a>
                    )}
                    {selectedAffiliate.socialLinks.instagram && (
                      <a
                        href={selectedAffiliate.socialLinks.instagram.startsWith('http') ? selectedAffiliate.socialLinks.instagram : `https://${selectedAffiliate.socialLinks.instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1 bg-pink-50 text-pink-700 rounded-lg text-xs font-semibold hover:bg-pink-100 transition-colors inline-flex items-center space-x-1"
                      >
                        <span>📷 Instagram</span>
                      </a>
                    )}
                    {selectedAffiliate.socialLinks.linkedin && (
                      <a
                        href={selectedAffiliate.socialLinks.linkedin.startsWith('http') ? selectedAffiliate.socialLinks.linkedin : `https://${selectedAffiliate.socialLinks.linkedin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-colors inline-flex items-center space-x-1"
                      >
                        <span>💼 LinkedIn</span>
                      </a>
                    )}
                    {selectedAffiliate.socialLinks.communityOrWebsite && (
                      <a
                        href={selectedAffiliate.socialLinks.communityOrWebsite.startsWith('http') ? selectedAffiliate.socialLinks.communityOrWebsite : `https://${selectedAffiliate.socialLinks.communityOrWebsite}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1 bg-gray-100 text-gray-800 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors inline-flex items-center space-x-1"
                      >
                        <span>🌐 Community / Website</span>
                      </a>
                    )}
                  </div>
                )}
              </div>

              {/* ── NEW SECTION: Policy Agreements & Compliance ── */}
              <div>
                <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-2 flex items-center space-x-1.5">
                  <span>⚖️</span>
                  <span>Compliance & Agreements</span>
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div className={`p-2.5 rounded-lg border text-center ${
                    selectedAffiliate.agreements?.termsAccepted !== false
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : 'bg-gray-50 text-gray-400 border-gray-200'
                  }`}>
                    <span className="font-bold text-sm block">✓</span>
                    <span className="text-[11px] font-semibold">Terms Accepted</span>
                  </div>
                  <div className={`p-2.5 rounded-lg border text-center ${
                    selectedAffiliate.agreements?.disclosePartnership !== false
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : 'bg-gray-50 text-gray-400 border-gray-200'
                  }`}>
                    <span className="font-bold text-sm block">✓</span>
                    <span className="text-[11px] font-semibold">Partnership Disclosed</span>
                  </div>
                  <div className={`p-2.5 rounded-lg border text-center ${
                    selectedAffiliate.agreements?.noSpamPolicy !== false
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : 'bg-gray-50 text-gray-400 border-gray-200'
                  }`}>
                    <span className="font-bold text-sm block">✓</span>
                    <span className="text-[11px] font-semibold">Anti-Spam Policy</span>
                  </div>
                  <div className={`p-2.5 rounded-lg border text-center ${
                    selectedAffiliate.agreements?.noBrandBidding !== false
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : 'bg-gray-50 text-gray-400 border-gray-200'
                  }`}>
                    <span className="font-bold text-sm block">✓</span>
                    <span className="text-[11px] font-semibold">No Brand Bidding</span>
                  </div>
                </div>
              </div>

              {/* Payout & Banking Details */}
              <div>
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center space-x-1.5">
                  <span>🏦</span>
                  <span>Banking & Payout Method</span>
                </h4>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 text-sm space-y-1">
                  {selectedAffiliate.payoutInfo?.upiId && (
                    <div className="flex justify-between py-1 border-b border-gray-200">
                      <span className="text-gray-500 text-xs">UPI ID:</span>
                      <span className="font-mono font-semibold text-gray-900 text-xs">{selectedAffiliate.payoutInfo.upiId}</span>
                    </div>
                  )}

                  {selectedAffiliate.payoutInfo?.bankName && (
                    <div className="flex justify-between py-1 border-b border-gray-200">
                      <span className="text-gray-500 text-xs">Bank Name:</span>
                      <span className="font-semibold text-gray-900 text-xs">{selectedAffiliate.payoutInfo.bankName}</span>
                    </div>
                  )}

                  {selectedAffiliate.payoutInfo?.accountNumber && (
                    <div className="flex justify-between py-1 border-b border-gray-200">
                      <span className="text-gray-500 text-xs">Account Number:</span>
                      <span className="font-mono font-semibold text-gray-900 text-xs">{selectedAffiliate.payoutInfo.accountNumber}</span>
                    </div>
                  )}

                  {selectedAffiliate.payoutInfo?.ifscCode && (
                    <div className="flex justify-between py-1 border-b border-gray-200">
                      <span className="text-gray-500 text-xs">IFSC Code:</span>
                      <span className="font-mono font-semibold text-gray-900 text-xs">{selectedAffiliate.payoutInfo.ifscCode}</span>
                    </div>
                  )}

                  {selectedAffiliate.payoutInfo?.accountHolderName && (
                    <div className="flex justify-between py-1">
                      <span className="text-gray-500 text-xs">Holder Name:</span>
                      <span className="font-semibold text-gray-900 text-xs">{selectedAffiliate.payoutInfo.accountHolderName}</span>
                    </div>
                  )}

                  {!selectedAffiliate.payoutInfo?.upiId && !selectedAffiliate.payoutInfo?.accountNumber && (
                    <p className="text-xs text-gray-400 italic">No payout method configured yet by this partner.</p>
                  )}
                </div>
              </div>

              {/* Status Timestamps / Rejection Reason */}
              {selectedAffiliate.approvedAt && (
                <div className="bg-emerald-50/70 p-3 rounded-lg border border-emerald-100 text-xs text-emerald-800 flex items-center justify-between">
                  <span>✅ Approved & Activated</span>
                  <span className="font-medium">
                    {new Date(selectedAffiliate.approvedAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              )}

              {selectedAffiliate.rejectionReason && (
                <div className="bg-red-50 p-3 rounded-lg border border-red-200 text-xs text-red-800">
                  <span className="font-bold block mb-0.5">Rejection Reason:</span>
                  <p>{selectedAffiliate.rejectionReason}</p>
                </div>
              )}

              {/* Internal Notes */}
              {selectedAffiliate.notes && (
                <div>
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Admin Notes</h4>
                  <p className="text-xs text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100">
                    {selectedAffiliate.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Footer Buttons */}
            <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <div>
                {selectedAffiliate.status === 'pending' ? (
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => handleOpenApproveModal(selectedAffiliate)}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm transition-colors flex items-center space-x-1.5"
                    >
                      <span>✓</span>
                      <span>Approve & Configure Rates</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRejectAffiliate(selectedAffiliate)}
                      className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 text-xs font-semibold rounded-lg transition-colors flex items-center space-x-1"
                    >
                      <span>✕</span>
                      <span>Reject</span>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowDetailModal(false);
                        handleOpenEdit(selectedAffiliate);
                      }}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-semibold rounded-lg transition-colors"
                    >
                      ✏️ Edit Settings
                    </button>
                    {(selectedAffiliate.status === 'suspended' || selectedAffiliate.status === 'rejected') && (
                      <button
                        type="button"
                        onClick={() => handleOpenApproveModal(selectedAffiliate)}
                        className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors"
                      >
                        Re-Approve & Activate
                      </button>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-gray-900 hover:bg-black text-white text-xs font-semibold rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL: PROCESS PAYOUT (APPROVE / REJECT / COMPLETE)
      ───────────────────────────────────────────────────────────── */}
      {showPayoutModal && selectedPayout && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden border border-gray-100">
            <div className="p-5 bg-gradient-to-r from-emerald-600 to-teal-700 text-white flex justify-between items-center">
              <div>
                <h3 className="text-base font-bold">Process Payout Request</h3>
                <p className="text-xs text-emerald-100 mt-0.5">
                  Amount: ₹{selectedPayout.amount?.toLocaleString('en-IN')}
                </p>
              </div>
              <button
                onClick={() => setShowPayoutModal(false)}
                className="text-white hover:text-gray-200 p-1 text-xl focus:outline-none"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitPayout} className="p-6 space-y-4">
              <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-500">Beneficiary:</span>
                  <span className="font-semibold text-gray-900">{selectedPayout.affiliateId?.name || 'Partner'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment Channel:</span>
                  <span className="font-semibold uppercase text-gray-900">{selectedPayout.payoutMethod}</span>
                </div>
                {selectedPayout.payoutDetails?.upiId && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">UPI ID:</span>
                    <span className="font-mono font-semibold text-blue-600">{selectedPayout.payoutDetails.upiId}</span>
                  </div>
                )}
                {selectedPayout.payoutDetails?.accountNumber && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Bank Account:</span>
                    <span className="font-mono font-semibold text-gray-900">{selectedPayout.payoutDetails.accountNumber}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Update Status</label>
                <select
                  value={payoutForm.status}
                  onChange={(e) => setPayoutForm({ ...payoutForm, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  <option value="completed">Completed (Funds Disbursed & Deducted)</option>
                  <option value="processing">Processing (In Progress)</option>
                  <option value="rejected">Rejected (Cancel Request)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Bank Reference / UTR Number
                </label>
                <input
                  type="text"
                  value={payoutForm.transactionReference}
                  onChange={(e) => setPayoutForm({ ...payoutForm, transactionReference: e.target.value })}
                  placeholder="e.g. UTR309482903 or IMPS-98321"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Payout Note (Optional)</label>
                <textarea
                  rows="2"
                  value={payoutForm.notes}
                  onChange={(e) => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                  placeholder="Any notes for this disbursement..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              <div className="pt-3 border-t border-gray-100 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowPayoutModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Updating...' : 'Save & Confirm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL: CONFIRM DELETE AFFILIATE
      ───────────────────────────────────────────────────────────── */}
      {showDeleteModal && affiliateToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden border border-gray-100 p-6">
            <div className="text-center">
              <div className="h-12 w-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto text-2xl mb-4">
                ⚠️
              </div>
              <h3 className="text-lg font-bold text-gray-900">Delete Affiliate Partner?</h3>
              <p className="text-xs text-gray-500 mt-2">
                Are you sure you want to delete <span className="font-semibold text-gray-800">{affiliateToDelete.name}</span> ({affiliateToDelete.referralCode})? This action cannot be undone.
              </p>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setAffiliateToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleDeleteAffiliate}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors disabled:opacity-50"
              >
                {submitting ? 'Deleting...' : 'Delete Affiliate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL: APPROVE AFFILIATE & CONFIGURE COMMISSION & DISCOUNT
      ───────────────────────────────────────────────────────────── */}
      {showApproveModal && affiliateToApprove && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 flex items-center justify-center p-4 animate-fade-in backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden border border-gray-100 transition-all transform scale-100">
            {/* Header */}
            <div className="p-5 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 text-white flex justify-between items-start">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-xl shadow-inner font-bold">
                  ✓
                </div>
                <div>
                  <h3 className="text-base font-bold tracking-tight">Approve Affiliate & Activate Coupon</h3>
                  <p className="text-xs text-emerald-100 mt-0.5">
                    Customize coupon code, commission rate, and buyer discount before going live
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowApproveModal(false);
                  setAffiliateToApprove(null);
                }}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 text-lg transition-colors focus:outline-none"
              >
                ✕
              </button>
            </div>

            {/* Applicant Summary Card */}
            <div className="bg-slate-50 border-b border-gray-100 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-gray-900">{affiliateToApprove.name}</div>
                  <div className="text-xs text-gray-500">{affiliateToApprove.email}</div>
                  {(affiliateToApprove.city || affiliateToApprove.state) && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      📍 {[affiliateToApprove.city, affiliateToApprove.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {affiliateToApprove.organizationOrClub && (
                    <div className="text-xs text-indigo-600 mt-0.5 font-medium">
                      Org: {affiliateToApprove.organizationOrClub} ({affiliateToApprove.audienceCategory || 'Content creator'})
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block">Application</span>
                  <span className="inline-block px-2.5 py-1 bg-amber-100 text-amber-800 rounded-md font-semibold text-xs border border-amber-300">
                    Pending Approval
                  </span>
                </div>
              </div>
            </div>

            {/* Form Content */}
            <form onSubmit={handleConfirmApprove} className="p-6 space-y-4">
              {/* 1. Coupon / Referral Code Input (Admin writes their own code) */}
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center space-x-1">
                    <span>🎟️ Assign Referral / Coupon Code</span>
                    <span className="text-emerald-600">*</span>
                  </label>
                  <span className="text-[11px] text-gray-400 font-medium">
                    Type your own code
                  </span>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    required
                    value={approveForm.couponCode}
                    onChange={(e) =>
                      setApproveForm({
                        ...approveForm,
                        couponCode: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '')
                      })
                    }
                    className="w-full pl-3 pr-28 py-2.5 border border-emerald-300 rounded-xl text-sm font-mono font-bold tracking-wider text-gray-900 uppercase focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm transition-all bg-white"
                    placeholder="e.g. CHARU10 or TAKUMI-CHARU"
                    autoFocus
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">
                      LIVE COUPON
                    </span>
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  Type the custom promo code you want to assign to this affiliate. It will be activated immediately upon approval.
                </p>
              </div>

              {/* 2. Commission Rate Input */}
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                    Affiliate Commission Rate (%) <span className="text-emerald-600">*</span>
                  </label>
                  <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                    Partner earns {approveForm.commissionRate || 0}%
                  </span>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    required
                    value={approveForm.commissionRate}
                    onChange={(e) =>
                      setApproveForm({ ...approveForm, commissionRate: e.target.value })
                    }
                    className="w-full pl-3 pr-10 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm transition-all"
                    placeholder="e.g. 10"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400 text-sm font-bold">
                    %
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  Commission percentage credited to this affiliate's wallet on every referral purchase.
                </p>

                {/* Quick Presets for Commission */}
                <div className="flex items-center space-x-1.5 mt-2">
                  <span className="text-[10px] text-gray-400 font-medium">Quick Presets:</span>
                  {[5, 10, 15, 20, 25].map((rate) => (
                    <button
                      key={rate}
                      type="button"
                      onClick={() => setApproveForm({ ...approveForm, commissionRate: rate })}
                      className={`text-xs px-2.5 py-0.5 rounded font-medium border transition-colors ${
                        Number(approveForm.commissionRate) === rate
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* 3. Discount Percentage Input */}
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                    Buyer Discount Percentage (%) <span className="text-blue-600">*</span>
                  </label>
                  <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                    Buyer saves {approveForm.discountPercentage || 0}%
                  </span>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    required
                    value={approveForm.discountPercentage}
                    onChange={(e) =>
                      setApproveForm({ ...approveForm, discountPercentage: e.target.value })
                    }
                    className="w-full pl-3 pr-10 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all"
                    placeholder="e.g. 10"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400 text-sm font-bold">
                    %
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  Discount applied at checkout when a customer enters this coupon code {approveForm.couponCode ? <strong className="font-mono text-gray-700">({approveForm.couponCode})</strong> : ''}.
                </p>

                {/* Quick Presets for Discount */}
                <div className="flex items-center space-x-1.5 mt-2">
                  <span className="text-[10px] text-gray-400 font-medium">Quick Presets:</span>
                  {[0, 5, 10, 15, 20].map((rate) => (
                    <button
                      key={rate}
                      type="button"
                      onClick={() => setApproveForm({ ...approveForm, discountPercentage: rate })}
                      className={`text-xs px-2.5 py-0.5 rounded font-medium border transition-colors ${
                        Number(approveForm.discountPercentage) === rate
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* 4. Real-time Impact Breakdown */}
              <div className="bg-gradient-to-br from-emerald-50/70 via-blue-50/70 to-indigo-50/70 p-3.5 rounded-xl border border-emerald-100/90 text-xs space-y-2 shadow-sm">
                <div className="text-[11px] font-bold text-gray-800 uppercase tracking-wider flex items-center justify-between">
                  <span className="flex items-center space-x-1">
                    <span>⚡</span>
                    <span>Live Simulation (₹1,000 Order)</span>
                  </span>
                  <span className={`font-mono px-2 py-0.5 rounded font-bold text-xs border ${
                    approveForm.couponCode
                      ? 'bg-white text-emerald-800 border-emerald-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'
                  }`}>
                    {approveForm.couponCode || 'ENTER CODE ABOVE'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1 border-t border-emerald-100">
                  <div className="bg-white/90 p-2 rounded-lg border border-gray-100">
                    <div className="text-[10px] text-gray-500 font-medium">Customer Pays</div>
                    <div className="text-sm font-bold text-gray-900 mt-0.5">
                      ₹{Math.round(1000 * (1 - (Number(approveForm.discountPercentage) || 0) / 100))}
                    </div>
                    <div className="text-[10px] text-blue-600 font-semibold">
                      Saves ₹{Math.round(1000 * ((Number(approveForm.discountPercentage) || 0) / 100))} ({approveForm.discountPercentage || 0}% OFF)
                    </div>
                  </div>
                  <div className="bg-white/90 p-2 rounded-lg border border-gray-100">
                    <div className="text-[10px] text-gray-500 font-medium">Affiliate Earns</div>
                    <div className="text-sm font-bold text-emerald-700 mt-0.5">
                      ₹{Math.round((1000 * (1 - (Number(approveForm.discountPercentage) || 0) / 100) * (Number(approveForm.commissionRate) || 0)) / 100)}
                    </div>
                    <div className="text-[10px] text-emerald-600 font-semibold">
                      {approveForm.commissionRate || 0}% cut credited to wallet
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Action Buttons */}
              <div className="pt-2 flex items-center justify-end space-x-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    setShowApproveModal(false);
                    setAffiliateToApprove(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={approving}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all inline-flex items-center space-x-1.5 disabled:opacity-50"
                >
                  {approving ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                      <span>Activating Coupon...</span>
                    </>
                  ) : (
                    <>
                      <span>✓</span>
                      <span>Approve & Activate Coupon</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Affiliates;
